import { useEnvironmentVariables, useRuntimeVariables } from 'app-kit'
import React, { FC, ReactNode } from 'react'

import { Auth0AuthContext } from './vendors/Auth0AuthContext'
import { AuthProvider } from './vendors/Auth0Provider'
import { CognitoAuthContext } from './vendors/CognitoAuthContext'
import { CognitoProvider } from './vendors/CognitoProvider'
import { KeycloakAuthContext } from './vendors/KeycloakAuthContext'
import { KeycloakProvider } from './vendors/KeycloakProvider'
import { SecondFrontAuthContext } from './vendors/SecondFrontAuthContext'

/**
 * A high-level provider used to simplify authentication and authorization.
 * Used by other high-level providers like RolodexProvider and DSAPIProvider
 * to determine how to properly tag queries
 * @param children                Children of this component
 * @constructor
 */
export const GeckoAuthProvider: FC<{
  children: ReactNode
}> = ({ children }) => {
  const {
    BASE_PATH,
    RUNNING_ON,
    AWS_REGION,
    AUTH0_CLIENT_ID,
    COGNITO_BASE_DOMAIN,
    COGNITO_CLIENT_ID,
    COGNITO_USER_POOL_ID,
    KEYCLOAK_CLIENT_ID,
    KEYCLOAK_DOMAIN,
    KEYCLOAK_REALM,
  } = useRuntimeVariables()
  const { IS_GOV_ENVIRONMENT, IS_ADNOC_ENVIRONMENT } = useEnvironmentVariables()

  if (RUNNING_ON === '2f')
    return <SecondFrontAuthContext>{children}</SecondFrontAuthContext>

  if (RUNNING_ON === 'eks')
    return (
      <CognitoProvider
        baseDomain={COGNITO_BASE_DOMAIN}
        clientId={COGNITO_CLIENT_ID}
        awsRegion={AWS_REGION}
        cognitoUserPoolId={COGNITO_USER_POOL_ID}
        basePath={BASE_PATH}>
        <CognitoAuthContext>{children}</CognitoAuthContext>
      </CognitoProvider>
    )

  if (IS_GOV_ENVIRONMENT || IS_ADNOC_ENVIRONMENT)
    return (
      <KeycloakProvider
        clientId={KEYCLOAK_CLIENT_ID}
        keycloakDomain={KEYCLOAK_DOMAIN}
        keycloakRealm={KEYCLOAK_REALM}
        basePath={BASE_PATH}>
        <KeycloakAuthContext>{children}</KeycloakAuthContext>
      </KeycloakProvider>
    )

  return (
    // the AuthProvider in gecko-ui hardcodes its own auth0 audience/domain
    // this is used when making the initial authentication request
    <AuthProvider
      clientId={AUTH0_CLIENT_ID}
      basePath={BASE_PATH}
      useRefreshTokens={true}
      useRefreshTokensFallback={false}>
      <Auth0AuthContext>{children}</Auth0AuthContext>
    </AuthProvider>
  )
}
